import api, { UPLOAD_TIMEOUT } from './api'

export interface Post {
  id: number
  user: {
    id: number
    firstName: string
    lastName: string
    username: string
    profileImage?: string
  }
  content: string
  postType: 'TEXT' | 'IMAGE' | 'VIDEO'
  media?: PostMedia[]
  likeCount: number
  commentCount: number
  shareCount: number
  reshareCount: number
  viewCount: number
  visibility: 'PUBLIC' | 'FRIENDS' | 'PRIVATE'
  isLiked: boolean
  isSaved: boolean
  isReshared?: boolean
  reactionType?: string
  reactionCounts?: Record<string, number>
  hashtags?: string[]
  comments?: Comment[]
  createdAt: string
  updatedAt: string
  // Only present when this post was reshared (reposted) by the profile owner —
  // the timestamp the reshare happened (not the original post's createdAt).
  // Drives the "Reposted" badge + newest-activity sort on the profile Posts tab.
  resharedAt?: string
}

export interface PostMedia {
  id: number
  mediaUrl: string
  mediaType: 'IMAGE' | 'VIDEO'
  width?: number
  height?: number
}

export interface Comment {
  id: number
  postId: number
  userId: number
  user: {
    id: number
    firstName: string
    lastName: string
    username: string
    profileImage?: string
  }
  content: string
  likeCount: number
  replyCount: number
  replies?: Comment[]
  createdAt: string
}

export interface CreatePostRequest {
  content: string
  postType: 'TEXT' | 'IMAGE' | 'VIDEO'
  visibility?: 'PUBLIC' | 'FRIENDS' | 'PRIVATE'
  mediaIds?: number[]
  hashtags?: string[]
}

const postService = {
  // Get feed posts with pagination (slower - uses OFFSET)
  getFeedPosts: async (page: number = 0, size: number = 20) => {
    const response = await api.get(`/posts/feed?page=${page}&size=${size}`)
    return response.data
  },

  // Get feed posts with cursor-based pagination (FASTER)
  getFeedPostsCursor: async (cursor?: number, size: number = 15) => {
    const params = new URLSearchParams()
    if (cursor) params.append('cursor', cursor.toString())
    params.append('size', size.toString())
    const response = await api.get(`/posts/feed/cursor?${params.toString()}`)
    return response.data
  },

  // Get single post
  getPost: async (postId: number) => {
    const response = await api.get(`/posts/${postId}`)
    return response.data
  },

  // Create post
  createPost: async (data: CreatePostRequest) => {
    const response = await api.post('/posts', data)
    return response.data
  },

  // Create post with media
  createPostWithMedia: async (formData: FormData) => {
    const response = await api.post('/posts/with-media', formData, {
      timeout: UPLOAD_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // Upload media
  uploadMedia: async (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/media/upload', formData, {
      timeout: UPLOAD_TIMEOUT,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  // Like/Unlike post (toggle)
  toggleLike: async (postId: number) => {
    const response = await api.post(`/posts/${postId}/like`)
    return response.data
  },

  // React to post (Facebook-style reactions)
  react: async (postId: number, reactionType: string) => {
    const response = await api.post(`/posts/${postId}/react`, { reactionType })
    return response.data
  },

  // Remove reaction
  removeReaction: async (postId: number) => {
    const response = await api.delete(`/posts/${postId}/react`)
    return response.data
  },

  // Get reaction details
  getReactions: async (postId: number) => {
    const response = await api.get(`/posts/${postId}/reactions`)
    return response.data
  },

  // Save/Bookmark post
  toggleSave: async (postId: number) => {
    const response = await api.post(`/posts/${postId}/save`)
    return response.data
  },

  // Get saved posts
  getSavedPosts: async (page: number = 0, size: number = 20) => {
    const response = await api.get(`/posts/saved?page=${page}&size=${size}`)
    return response.data
  },

  // Share post
  sharePost: async (postId: number) => {
    const response = await api.post(`/posts/${postId}/share`)
    return response.data
  },

  // Reshare post (idempotent)
  resharePost: async (postId: number) => {
    const response = await api.post(`/posts/${postId}/reshare`)
    return response.data
  },

  // Remove reshare from post
  unresharePost: async (postId: number) => {
    const response = await api.delete(`/posts/${postId}/reshare`)
    return response.data
  },

  // Delete post
  deletePost: async (postId: number) => {
    const response = await api.delete(`/posts/${postId}`)
    return response.data
  },

  // Get post comments
  getComments: async (postId: number, page: number = 0, size: number = 20) => {
    const response = await api.get(`/posts/${postId}/comments?page=${page}&size=${size}`)
    return response.data
  },

  // Add comment
  addComment: async (postId: number, content: string, parentId?: number) => {
    const response = await api.post(`/posts/${postId}/comments`, { content, parentId })
    return response.data
  },

  // Delete comment
  deleteComment: async (commentId: number) => {
    const response = await api.delete(`/comments/${commentId}`)
    return response.data
  },

  // Like comment
  likeComment: async (postId: number, commentId: number) => {
    const response = await api.post(`/posts/${postId}/comments/${commentId}/like`)
    return response.data
  },

  // Get replies for a comment
  getReplies: async (postId: number, commentId: number) => {
    const response = await api.get(`/posts/${postId}/comments/${commentId}/replies`)
    return response.data
  },

  // Get user posts
  getUserPosts: async (userId: number, page: number = 0, size: number = 20) => {
    const response = await api.get(`/posts/user/${userId}?page=${page}&size=${size}`)
    return response.data
  },

  // Get posts this user has reshared (reposted from others). Each Post carries
  // resharedAt + the ORIGINAL author in post.user (Instagram-style — the post is
  // never duplicated). Drives the "Reposted" items on the profile Posts tab.
  getResharedPosts: async (userId: number, page: number = 0, size: number = 20) => {
    const response = await api.get(`/posts/user/${userId}/reshared?page=${page}&size=${size}`)
    return response.data
  }
}

export default postService